import type { GoogleAuthMode, MailboxOauthMode, SharePayload } from './types.js';

/** Cookie OAuth stays an `<a href>` on web; this function is never called. */
export const googleAuth: GoogleAuthMode = 'link';

/** Same origin, real session cookie: the plain navigation works here. */
export const mailboxOauth: MailboxOauthMode = 'redirect';

/** A browser can be on anything, so the screen's width says whether it is a phone. */
export const handheld = false;

const SECURE_PREFIX = 'worki.secure.';

/**
 * Today's DOM behaviour, compiled into the web (and foreign-origin spike) bundle.
 */
export async function saveBytes(blob: Blob, filename: string): Promise<void> {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  // Firefox follows a click only on an anchor that is in the document.
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Safari reads the blob after the click returns, so the URL cannot be
  // released on the next line.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export async function openExternal(url: string): Promise<void> {
  window.open(url, '_blank', 'noopener');
}

export async function share(payload: SharePayload): Promise<boolean> {
  const sharer = navigator.share?.bind(navigator);
  if (!sharer) return false;
  try {
    await sharer(payload);
    return true;
  } catch {
    return false;
  }
}

export async function copyText(s: string): Promise<boolean> {
  const write = navigator.clipboard?.writeText?.bind(navigator.clipboard);
  if (!write) return false;
  try {
    await write(s);
    return true;
  } catch {
    return false;
  }
}

export async function secureGet(key: string): Promise<string | null> {
  try {
    return localStorage.getItem(SECURE_PREFIX + key);
  } catch {
    return null;
  }
}

export async function secureSet(key: string, value: string): Promise<void> {
  try {
    localStorage.setItem(SECURE_PREFIX + key, value);
  } catch {
    // Storage blocked — the session lasts until reload at most.
  }
}

export async function secureDelete(key: string): Promise<void> {
  try {
    localStorage.removeItem(SECURE_PREFIX + key);
  } catch {
    // Same as set: nothing we can do if storage is closed.
  }
}

export function onNetworkOnline(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}

export async function setStatusBarColor(hex: string, _dark: boolean): Promise<void> {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', hex);
}

export async function googleSignIn(): Promise<{ idToken: string }> {
  throw new Error('Web Google sign-in is a cookie OAuth navigation, not a plugin call');
}
